import { createSimpleMutator } from '@superbet-group/web.lib.vue-utilities';
import {
    ActionContext,
    MutationTree,
} from 'vuex';

import { IState } from '@app/store';

import {
    MarketsState,
    TOP_WINNERS_TABS,
} from './types';
import { CustomMarketGroup } from '@modules/eventGrid/components/MarketLayouts/MarketLayoutTypes';
import OfferPresentationService from '@core/services/offerPresentation/OfferPresentationService';
import { asyncErrorGuard } from '@core/utils/ErrorGuard';
import deepFreeze from '@core/utils/dataTransformation/deepFreeze';

function getDefaultState(): MarketsState {
    return {
        selectedMarketGroupId: null,
        marketLayoutsConfig: undefined,
        marketLayouts: [],
        hasSetMarketLayouts: false,
        marketLayoutsUserId: undefined,
        nonFinalMarketsConfig: undefined,
        popularAccasConfig: undefined,
        topWinnersConfig: undefined,
        topWinnersSelectedTab: TOP_WINNERS_TABS.DAILY,
    };
}

const mutationTypes = {
    SET_SELECTED_MARKET_GROUP_ID: 'SET_SELECTED_MARKET_GROUP_ID',
    SET_MARKET_LAYOUTS_CONFIG: 'SET_MARKET_LAYOUTS_CONFIG',
    SET_MARKET_LAYOUTS: 'SET_MARKET_LAYOUTS',
    SET_HAS_SET_MARKET_LAYOUTS: 'SET_HAS_SET_MARKET_LAYOUTS',
    SET_MARKET_LAYOUTS_USER_ID: 'SET_MARKET_LAYOUTS_USER_ID',
    SET_NON_FINAL_MARKETS_CONFIG: 'SET_NON_FINAL_MARKETS_CONFIG',
    SET_POPULAR_ACCAS_CONFIG: 'SET_POPULAR_ACCAS_CONFIG',
    SET_TOP_WINNERS_CONFIG: 'SET_TOP_WINNERS_CONFIG',
    SET_TOP_WINNERS_SELECTED_TAB: 'SET_TOP_WINNERS_SELECTED_TAB',
};

const mutations: MutationTree<MarketsState> = {
    [mutationTypes.SET_SELECTED_MARKET_GROUP_ID]: createSimpleMutator<MarketsState, 'selectedMarketGroupId'>('selectedMarketGroupId'),
    [mutationTypes.SET_MARKET_LAYOUTS_CONFIG]: createSimpleMutator<MarketsState, 'marketLayoutsConfig'>('marketLayoutsConfig'),
    [mutationTypes.SET_MARKET_LAYOUTS]: createSimpleMutator<MarketsState, 'marketLayouts'>('marketLayouts'),
    [mutationTypes.SET_HAS_SET_MARKET_LAYOUTS]: createSimpleMutator<MarketsState, 'hasSetMarketLayouts'>('hasSetMarketLayouts'),
    [mutationTypes.SET_MARKET_LAYOUTS_USER_ID]: createSimpleMutator<MarketsState, 'marketLayoutsUserId'>('marketLayoutsUserId'),
    [mutationTypes.SET_NON_FINAL_MARKETS_CONFIG]: createSimpleMutator<MarketsState, 'nonFinalMarketsConfig'>('nonFinalMarketsConfig'),
    [mutationTypes.SET_POPULAR_ACCAS_CONFIG]: createSimpleMutator<MarketsState, 'popularAccasConfig'>('popularAccasConfig'),
    [mutationTypes.SET_TOP_WINNERS_CONFIG]: createSimpleMutator<MarketsState, 'topWinnersConfig'>('topWinnersConfig'),
    [mutationTypes.SET_TOP_WINNERS_SELECTED_TAB]: createSimpleMutator<MarketsState, 'topWinnersSelectedTab'>('topWinnersSelectedTab'),
};

const store = {
    namespaced: true,
    state: getDefaultState(),
    mutations,
    actions: {
        setSelectedMarketGroupId(
            { commit }: ActionContext<MarketsState, IState>,
            marketGroupId: number | null,
        ): void {
            commit(mutationTypes.SET_SELECTED_MARKET_GROUP_ID, marketGroupId);
        },

        fetchMarketLayoutsConfig: asyncErrorGuard(async (
            { commit }: ActionContext<MarketsState, IState>,
        ) => {
            const config = await OfferPresentationService.getMarketLayouts();

            commit(mutationTypes.SET_MARKET_LAYOUTS_CONFIG, deepFreeze(config));
        }),

        setMarketLayouts(
            { commit }: ActionContext<MarketsState, IState>,
            marketLayouts: CustomMarketGroup[],
        ): void {
            commit(mutationTypes.SET_MARKET_LAYOUTS, marketLayouts);
        },

        setHasSetMarketLayouts(
            { commit }: ActionContext<MarketsState, IState>,
            value: boolean,
        ): void {
            commit(mutationTypes.SET_HAS_SET_MARKET_LAYOUTS, value);
        },

        setMarketLayoutsUserId(
            { commit }: ActionContext<MarketsState, IState>,
            userId?: number,
        ): void {
            commit(mutationTypes.SET_MARKET_LAYOUTS_USER_ID, userId);
        },

        fetchNonFinalMarketsConfig: asyncErrorGuard(async (
            { commit }: ActionContext<MarketsState, IState>,
        ) => {
            const nonFinalConfig = await OfferPresentationService.getNonFinalMarketsOnHomepage();

            commit(mutationTypes.SET_NON_FINAL_MARKETS_CONFIG, nonFinalConfig);
        }),

        fetchPopularAccasConfig: asyncErrorGuard(async (
            { commit }: ActionContext<MarketsState, IState>,
        ) => {
            const popularAccasConfig = await OfferPresentationService.getPopularAccumulators();

            commit(mutationTypes.SET_POPULAR_ACCAS_CONFIG, popularAccasConfig);
        }),
        fetchTopWinnersConfig: asyncErrorGuard(async (
            { commit }: ActionContext<MarketsState, IState>,
        ) => {
            const topWinnersConfig = await OfferPresentationService.getTopWinners();
            const TAB_NAMES = ['daily', 'weekly', 'monthly'] as const;

            // todo: will fix this on the endpoint
            TAB_NAMES.forEach((tabName) => {
                topWinnersConfig[tabName] = topWinnersConfig[tabName].map((ticket) => {
                    return {
                        ...ticket,
                        id: crypto.randomUUID(),
                    };
                });
            });

            commit(mutationTypes.SET_TOP_WINNERS_CONFIG, topWinnersConfig);
        }),
        setTopWinnersSelectedTab(
            { commit }: ActionContext<MarketsState, IState>,
            selectedTab: string,
        ): void {
            commit(mutationTypes.SET_TOP_WINNERS_SELECTED_TAB, selectedTab);
        },
    },
};

export default store;
export type SportOfferMarketsStore = typeof store;
